import Service from 'react-reservice';

import accountAPI, {
  IAssetStatisticResult,
  IProfitStatistic,
  IStatisticsAccount
} from '@webtrader/core/api/statistics/account';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { authService } from '@webtrader/core/services/auth';

interface IFetchStatisticsParams {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}
interface IStatisticsContext {
  account?: IStatisticsAccount;
  profits?: IProfitStatistic[];
  assets?: IAssetStatisticResult;
  loading?: boolean;
}

const defaultContext: IStatisticsContext = {
  loading: true
};

class StatisticsService extends Service<IStatisticsContext> {
  private pnlAbortController = new AbortController();
  private assetAbortController = new AbortController();
  private statisticsAbortController = new AbortController();

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = () => {
    this.fetchAccountStatistics();
  };

  public refreshStatistics = (params?: IFetchStatisticsParams) => {
    this.fetchAccountStatistics(false, params);
  };

  private fetchAccountStatistics = async (loading = true, params?: IFetchStatisticsParams) => {
    const { onSuccess, onError } = params || {};
    this.statisticsAbortController.abort();
    this.statisticsAbortController = new AbortController();
    try {
      this.context.loading = loading;
      const { result } = await accountAPI.statisticsAccount(this.statisticsAbortController.signal);
      const { accountBalance } = result || {};
      if (accountBalance) {
        authService.updateAccountBalances(accountBalance);
      }
      this.context.account = result || undefined;
      onSuccess?.();
    } catch (e) {
      // ignore aborted error
      if ((e as IApiError).isAborted) {
        return;
      }
      const { code, message } = e as IApiError;
      const errorMessage = mapResponseCodeToMessage(code, message);
      onError?.(errorMessage);
    } finally {
      this.context.loading = false;
    }
  };

  public fetchProfitStatistics = async () => {
    try {
      this.pnlAbortController.abort();
      this.pnlAbortController = new AbortController();

      const { result = [] } = await accountAPI.profitsStatistics({ days: 14 }, this.pnlAbortController.signal);

      this.context.profits = result || undefined;
    } catch {}
  };

  public fetchAssetsStatistics = async () => {
    try {
      this.assetAbortController.abort();
      this.assetAbortController = new AbortController();

      const { result } = await accountAPI.assetsStatistics(this.assetAbortController.signal);

      this.context.assets = result || undefined;
    } catch {}
  };
}

export default new StatisticsService();
